import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import barba from "@barba/core";

// Northfold Creators — theme script (vanilla JS · GSAP hero motion · Lenis smooth
// scroll · Barba page transitions). Everything is progressive enhancement: the
// page is fully readable without it (the `js` class on <html> gates the hidden
// initial states in the CSS), and with prefers-reduced-motion there is no
// smooth scroll and no page transition (normal navigation).
//
// Structure: one-time setup (header, menu, smooth scroll, toasts, transition
// curtain) and initPage(root), which wires everything inside <main> and is
// re-run by Barba after every page swap; its cleanups run before the swap.

document.addEventListener("DOMContentLoaded", () => {
  const RM = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  gsap.registerPlugin(ScrollTrigger);

  // ─── Smooth scroll (Lenis) driven by the GSAP ticker so ScrollTrigger stays in sync.
  // Wheel/trackpad only (touch keeps native momentum); off for reduced motion.
  let lenis = null;
  if (!RM) {
    lenis = new Lenis({
      duration: 1.25,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      anchors: true,
    });
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
  }

  // ─── Header + mobile menu (persist across page transitions) ─────────────────
  const hdr = $(".js-hdr");
  const burger = $(".js-burger");
  const panel = $(".js-mpanel");

  // Menu open/close: the panel unrolls from the header (clip-path), then the
  // links rise in one by one; closing plays a shorter reverse. `instant` skips
  // the motion (resize, page transition, reduced motion).
  let menuTl = null;
  const menuItems = () => $$(".mpanel__list li, .mpanel .btn", panel);
  const setMenuState = (open) => {
    burger.setAttribute("aria-expanded", String(open));
    document.body.classList.toggle("locked", open);
    if (lenis) (open ? lenis.stop() : lenis.start());
  };

  const openMenu = () => {
    if (!burger || !panel) return;
    if (menuTl) menuTl.kill();
    setMenuState(true);
    panel.hidden = false;
    const items = menuItems();
    if (RM) {
      gsap.set([panel, items], { clearProps: "all" });
    } else {
      menuTl = gsap.timeline({ defaults: { ease: "power3.out" } });
      menuTl
        .fromTo(panel, { clipPath: "inset(0% 0% 100% 0%)" }, { clipPath: "inset(0% 0% 0% 0%)", duration: 0.75, ease: "power3.inOut" })
        .fromTo(items, { y: 48, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, stagger: 0.08 }, 0.25)
        .set(panel, { clearProps: "clipPath" });
    }
    const first = $("a", panel);
    if (first) first.focus({ preventScroll: true });
  };

  const closeMenu = (instant = false) => {
    if (!burger || !panel || panel.hidden) return;
    if (menuTl) menuTl.kill();
    setMenuState(false);
    const done = () => {
      panel.hidden = true;
      gsap.set([panel, menuItems()], { clearProps: "all" });
    };
    if (RM || instant) {
      done();
      return;
    }
    menuTl = gsap.timeline({ onComplete: done, defaults: { ease: "power2.in" } });
    menuTl
      .to(menuItems(), { y: -24, opacity: 0, duration: 0.3, stagger: { each: 0.04, from: "end" } })
      .to(panel, { clipPath: "inset(0% 0% 100% 0%)", duration: 0.55, ease: "power3.inOut" }, 0.1);
  };

  if (burger && panel) {
    burger.addEventListener("click", () => {
      if (burger.getAttribute("aria-expanded") === "true") closeMenu();
      else openMenu();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && burger.getAttribute("aria-expanded") === "true") {
        closeMenu();
        burger.focus();
      }
    });
    // A link in the panel closes it (animated) — page transitions handle the rest.
    panel.addEventListener("click", (e) => {
      if (e.target.closest("a")) closeMenu();
    });
    window.addEventListener("resize", () => {
      if (window.innerWidth >= 1200) closeMenu(true);
    }, { passive: true });
  }

  // ─── Process rail + header state on scroll (passive, rAF-batched) ───────────
  const procFrame = () => {
    $$(".js-proc").forEach((p) => {
      const r = p.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.bottom < -200 || r.top > vh + 200) return;
      const k = RM ? 1 : Math.max(0, Math.min(1, (vh * 0.62 - r.top) / r.height));
      p.style.setProperty("--p", k.toFixed(3));
      $$(".step", p).forEach((s) => {
        const off = (s.offsetTop + 24) / r.height;
        s.classList.toggle("on", k >= off - 0.001);
      });
    });
  };
  let ticking = false;
  const scrollFrame = () => {
    ticking = false;
    if (hdr) hdr.classList.toggle("is-solid", window.scrollY > 40);
    procFrame();
  };
  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(scrollFrame);
    }
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  scrollFrame();

  // ─── Notifications (toast) — used by the demo forms and real CF7 forms ──────
  let toastRoot = null;
  const toast = (title, text, kind = "ok") => {
    if (!toastRoot) {
      toastRoot = document.createElement("div");
      toastRoot.className = "toasts";
      toastRoot.setAttribute("aria-live", "polite");
      toastRoot.setAttribute("role", "status");
      document.body.appendChild(toastRoot);
    }
    const el = document.createElement("div");
    el.className = `toast toast--${kind}`;
    const body = document.createElement("div");
    const b = document.createElement("b");
    b.textContent = title;
    const p = document.createElement("p");
    p.textContent = text;
    body.append(b, p);
    const close = document.createElement("button");
    close.type = "button";
    close.className = "toast__x";
    close.setAttribute("aria-label", "Close");
    close.textContent = "×";
    el.append(body, close);
    toastRoot.appendChild(el);
    requestAnimationFrame(() => el.classList.add("is-in"));
    const remove = () => {
      el.classList.remove("is-in");
      setTimeout(() => el.remove(), RM ? 0 : 350);
    };
    close.addEventListener("click", remove);
    setTimeout(remove, 6500);
  };

  // Real Contact Form 7 forms fire these DOM events; reuse the same notification.
  const cf7Toast = (e, kind) => {
    const box = e.target.closest(".js-fbox");
    if (!box) return;
    if (kind === "ok") toast(box.dataset.toastTitle, box.dataset.toastOk, "ok");
    else toast(box.dataset.toastErrTitle, box.dataset.toastErr, "error");
  };
  document.addEventListener("wpcf7mailsent", (e) => cf7Toast(e, "ok"));
  document.addEventListener("wpcf7invalid", (e) => cf7Toast(e, "error"));
  document.addEventListener("wpcf7mailfailed", (e) => cf7Toast(e, "error"));

  // Shared helpers for the demo forms.
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const URL_RE = /^https?:\/\/\S+\.\S+/;
  const setError = (field, input, message) => {
    const wrap = input.closest(".wpcf7-form-control-wrap") || field;
    let tip = field.querySelector(".wpcf7-not-valid-tip");
    if (message) {
      if (!tip) {
        tip = document.createElement("span");
        tip.className = "wpcf7-not-valid-tip";
        tip.setAttribute("role", "alert");
        wrap.after(tip);
        if (input.type === "checkbox") field.appendChild(tip);
      }
      tip.textContent = message;
      input.classList.add("wpcf7-not-valid");
      input.setAttribute("aria-invalid", "true");
    } else {
      if (tip) tip.remove();
      input.classList.remove("wpcf7-not-valid");
      input.removeAttribute("aria-invalid");
    }
  };

  const format = (n, dec) => n.toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const count = (el) => {
    if (el.dataset.done) return;
    el.dataset.done = "1";
    const raw = el.dataset.to;
    const to = parseFloat(raw);
    if (Number.isNaN(to)) return;
    const dec = (raw.split(".")[1] || "").length;
    const suf = el.dataset.suf || "";
    if (RM) {
      el.textContent = format(to, dec) + suf;
      return;
    }
    let t0 = null;
    const dur = 1600;
    const tick = (t) => {
      if (!t0) t0 = t;
      const k = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - k, 4);
      el.textContent = format(to * eased, dec) + suf;
      if (k < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // ─── Per-page behaviour ─────────────────────────────────────────────────────
  let cleanups = [];
  let ctx = null;

  const destroyPage = () => {
    cleanups.forEach((fn) => fn());
    cleanups = [];
    if (ctx) ctx.revert();
    ctx = null;
    ScrollTrigger.getAll().forEach((t) => t.kill());
  };

  const initPage = (root) => {
    // Scroll reveal: text eases in from the side it belongs to. Every text
    // element (except hero, long legal text, forms, tables) gets `.rv`;
    // left-hand columns slide from the left, right-hand from the right,
    // everything else rises. ≈32px, ≈1.2s, staggered per batch, once only.
    const SKIP = ".hero, .doc, form, table, .toast, .reel__track, .tl-box, .tbl-box, .legal--plain, [data-no-rv]";
    const TEXT = ".eyebrow, .crumb, h2, h3, h4, p, li, dt, dd, figcaption, blockquote, .link";
    $$(TEXT, root).forEach((el) => {
      if (el.closest(SKIP) || el.closest(".rv")) return; // inside an animated block already
      el.classList.add("rv");
    });
    const dirOf = (el) => {
      if (el.closest(".split__side, .sdet__side, .apply-grid__form, .contact-side, .cta-band__photo, .phero__aside")) return "right";
      if (el.matches(".eyebrow, .crumb") || el.closest(".split__main, .faq-grid__intro, .apply-grid__intro, .story-grid__intro, .reel__intro, .cta-band__txt")) return "left";
      return "up";
    };
    const revealables = $$(".rv, .tl-draw", root);
    revealables.forEach((el) => { if (el.classList.contains("rv") && !el.dataset.rv) el.dataset.rv = dirOf(el); });
    const countEls = $$("[data-to]", root);

    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver((entries) => {
        // Reveal everything that entered together in reading order, 110ms apart.
        const entered = entries
          .filter((entry) => entry.isIntersecting)
          .sort((x, y) => x.boundingClientRect.top - y.boundingClientRect.top || x.boundingClientRect.left - y.boundingClientRect.left);
        entered.forEach((entry, i) => {
          const el = entry.target;
          if (el.classList.contains("rv") && el.style.getPropertyValue("--d") === "") {
            el.style.setProperty("--d", String(Math.min(i, 6) * 1.2));
          }
          el.classList.add("in");
          if (el.hasAttribute("data-to")) count(el);
          io.unobserve(el);
        });
      }, { threshold: 0.15, rootMargin: "0px 0px -8% 0px" });
      revealables.forEach((el) => io.observe(el));
      countEls.forEach((el) => io.observe(el));
      cleanups.push(() => io.disconnect());
    } else {
      revealables.forEach((el) => el.classList.add("in"));
      countEls.forEach((el) => count(el));
    }
    // Failsafe: anything already on screen that the observer has not revealed
    // after 4s (throttled tab, odd browser) is shown rather than left invisible.
    const failsafe = setTimeout(() => {
      revealables.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add("in");
      });
    }, 4000);
    cleanups.push(() => clearTimeout(failsafe));

    // GSAP-driven motion (hero intro/parallax, inner-hero waves) lives in one
    // context so a page change reverts it in a single call.
    ctx = gsap.context(() => {
    // ─── Hero: slow GSAP intro + scroll parallax + cursor tilt ──────────────────
    const hero = $(".js-hero", root);
    if (hero) {
      const layers = $$(".hero__fold .ly", hero);
      const copy = $(".js-hero-copy", hero);
      const words = $$(".hero__title .w > span", hero);
      const foot = $(".hero__foot", hero);
      const cue = $(".scrollcue", hero);
      const sky = $(".hero__sky", hero);
      const stars = $(".hero__stars", hero);
      const eyebrow = $(".eyebrow", hero);

      if (RM) {
        hero.classList.add("go");
      } else {
        // Hidden start states (set before the first paint of the reveal).
        gsap.set(words, { yPercent: 110 });
        gsap.set(layers, { yPercent: 14, opacity: 0 });
        gsap.set([eyebrow, foot, cue], { opacity: 0 });
        gsap.set(eyebrow, { y: 16 });
        gsap.set(foot, { y: 28 });
        gsap.set([sky, stars], { opacity: 0 });
        hero.classList.add("go"); // un-hides the masked words; GSAP now owns the motion

        // Intro: dawn → ridgelines rise far-to-near → headline → copy. ~6s in all.
        const intro = gsap.timeline({ delay: 0.3, defaults: { ease: "power3.out" } });
        intro
          .to([sky, stars], { opacity: 1, duration: 2.4, ease: "power1.inOut" }, 0)
          .to(layers, { yPercent: 0, opacity: 1, duration: 3, ease: "power2.out", stagger: 0.32 }, 0.2)
          .to(eyebrow, { opacity: 1, y: 0, duration: 1.4 }, 1.4)
          .to(words, { yPercent: 0, duration: 2, ease: "power4.out", stagger: 0.2 }, 1.7)
          .to(foot, { opacity: 1, y: 0, duration: 1.6 }, ">-1.2")
          .to(cue, { opacity: 1, duration: 1.2 }, ">-0.8");

        // Scroll parallax: far layers lag more, smoothed by `scrub`.
        const dist = () => window.innerHeight * 1.2;
        layers.forEach((layer) => {
          const depth = parseFloat(layer.dataset.depth || "0"); // 0 far … 1 near
          gsap.to(layer, {
            y: () => dist() * (0.55 - depth * 0.5),
            ease: "none",
            scrollTrigger: { trigger: hero, start: "top top", end: () => `+=${dist()}`, scrub: 1.4, invalidateOnRefresh: true },
          });
        });
        if (copy) {
          gsap.to(copy, {
            y: () => dist() * 0.25,
            opacity: 0,
            ease: "none",
            scrollTrigger: { trigger: hero, start: "top top", end: () => `+=${window.innerHeight * 0.75}`, scrub: 1.4, invalidateOnRefresh: true },
          });
        }

        // Cursor tilt (mouse only): the nearest layer shifts ±12px, eased.
        if (window.matchMedia("(pointer: fine)").matches) {
          const movers = layers.map((layer) => ({
            to: gsap.quickTo(layer, "x", { duration: 1.8, ease: "power3.out" }),
            depth: parseFloat(layer.dataset.depth || "0"),
          }));
          hero.addEventListener("pointermove", (e) => {
            const r = hero.getBoundingClientRect();
            const nx = (e.clientX - r.left) / r.width - 0.5;
            movers.forEach((m) => m.to(nx * 24 * m.depth));
          }, { passive: true });
          hero.addEventListener("pointerleave", () => movers.forEach((m) => m.to(0)));
        }
      }
    }

    // ─── Inner-page hero: ridgeline rises in, then rolls like waves ─────────────
    // Each ridge layer is a polyline; every frame its points are offset by a
    // travelling sine wave (own wavelength, speed, direction and height per
    // layer), so the horizon undulates slowly. Runs only while the hero is on
    // screen, and is skipped entirely with prefers-reduced-motion.
    $$(".js-phero", root).forEach((ph) => {
      const paths = $$(".phero__fold path", ph);
      if (!paths.length || RM) return;

      gsap.from(paths, { yPercent: 35, opacity: 0, duration: 2.4, ease: "power3.out", stagger: 0.28, delay: 0.2 });

      const waves = paths.map((path, i) => {
        const pts = [];
        (path.getAttribute("d") || "").replace(/[ML]\s*(-?[\d.]+)[ ,]+(-?[\d.]+)/g, (_, x, y) => {
          pts.push([parseFloat(x), parseFloat(y)]);
          return "";
        });
        return {
          path,
          pts,
          amp: [5, 8, 11][i % 3],              // viewBox units (the svg is 240 high)
          len: [0.0062, 0.0046, 0.0079][i % 3], // spatial frequency
          speed: [0.55, 0.42, 0.7][i % 3] * (i % 2 ? -1 : 1),
          bob: [2.4, 3.2, 4][i % 3],
          phase: i * 1.7,
        };
      });

      let visible = true;
      if ("IntersectionObserver" in window) {
        const vio = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; });
        vio.observe(ph);
        cleanups.push(() => vio.disconnect());
      }

      const wave = (time) => {
        if (!visible || document.hidden) return;
        waves.forEach((w) => {
          const t = time * w.speed + w.phase;
          const bob = Math.sin(time * 0.6 + w.phase) * w.bob;
          let d = "";
          w.pts.forEach(([x, y], n) => {
            // Keep the two bottom anchors (y ≥ 900) fixed so the fill stays closed.
            const yy = y >= 900 ? y : y + Math.sin(x * w.len + t) * w.amp + bob;
            d += `${n ? "L" : "M"}${x},${yy.toFixed(1)} `;
          });
          w.path.setAttribute("d", `${d}Z`);
        });
      };
      gsap.ticker.add(wave);
      cleanups.push(() => gsap.ticker.remove(wave));
    });

    }, root);

    // Creator reel: arrows, drag, progress, counter ──────────────────────────
  const track = $(".js-reel-track", root);
  if (track) {
    const prev = $(".js-reel-prev");
    const next = $(".js-reel-next");
    const bar = $(".js-reel-bar");
    const counter = $(".js-reel-count");
    const cards = $$(".ccard", track);
    const gap = () => parseFloat(getComputedStyle(track).columnGap || "20") || 20;
    const cardW = () => cards[0].getBoundingClientRect().width + gap();
    const pad = (n) => String(n).padStart(2, "0");

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const k = max > 0 ? track.scrollLeft / max : 1;
      if (bar) bar.style.transform = `scaleX(${Math.max(0.08, k).toFixed(3)})`;
      if (prev) prev.disabled = track.scrollLeft < 4;
      if (next) next.disabled = track.scrollLeft > max - 4;
      if (counter) {
        const idx = Math.min(cards.length, Math.round(track.scrollLeft / cardW()) + 1);
        counter.textContent = `${pad(idx)} / ${pad(cards.length)}`;
      }
    };
    const step = (dir) => track.scrollBy({ left: dir * cardW(), behavior: RM ? "auto" : "smooth" });

    if (prev) prev.addEventListener("click", () => step(-1));
    if (next) next.addEventListener("click", () => step(1));
    track.addEventListener("scroll", () => requestAnimationFrame(update), { passive: true });
    track.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
    });

    let down = false, startX = 0, startLeft = 0;
    track.addEventListener("pointerdown", (e) => {
      if (e.pointerType !== "mouse") return;
      down = true;
      startX = e.clientX;
      startLeft = track.scrollLeft;
    });
    const onMove = (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) track.classList.add("is-drag");
      track.scrollLeft = startLeft - dx;
    };
    const onUp = () => {
      if (!down) return;
      down = false;
      if (track.classList.contains("is-drag")) {
        track.classList.remove("is-drag");
        track.scrollTo({ left: Math.round(track.scrollLeft / cardW()) * cardW(), behavior: RM ? "auto" : "smooth" });
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    track.addEventListener("dragstart", (e) => e.preventDefault());
    window.addEventListener("resize", update, { passive: true });
    cleanups.push(() => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("resize", update);
    });
    update();
  }

  // ─── Work filter chips (links to the sector archive without JS) ─────────────
  const chips = $$(".js-chip", root);
  const wcards = $(".js-wcards", root);
  if (chips.length && wcards) {
    const status = $(".js-work-count");
    chips.forEach((chip) => {
      chip.addEventListener("click", (e) => {
        e.preventDefault();
        const f = chip.dataset.f;
        let n = 0;
        chips.forEach((c) => c.setAttribute("aria-pressed", String(c === chip)));
        $$(".news-card", wcards).forEach((card) => {
          const show = f === "all" || card.dataset.cat === f;
          card.hidden = !show;
          if (show) { n += 1; card.classList.add("in"); }
        });
        if (status) status.textContent = `Showing ${n} ${n === 1 ? "case" : "cases"}`;
      });
    });
  }

  // ─── FAQ accordion ──────────────────────────────────────────────────────────
  $$(".js-faq-trigger", root).forEach((btn) => {
    btn.addEventListener("click", () => {
      const open = btn.getAttribute("aria-expanded") === "true";
      btn.setAttribute("aria-expanded", String(!open));
      const answer = document.getElementById(btn.getAttribute("aria-controls"));
      if (answer) answer.hidden = open;
    });
  });

  // ─── Demo forms: client-side validation + notification (nothing is sent) ────
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const URL_RE = /^https?:\/\/\S+\.\S+/;

  const setError = (field, input, message) => {
    const wrap = input.closest(".wpcf7-form-control-wrap") || field;
    let tip = field.querySelector(".wpcf7-not-valid-tip");
    if (message) {
      if (!tip) {
        tip = document.createElement("span");
        tip.className = "wpcf7-not-valid-tip";
        tip.setAttribute("role", "alert");
        wrap.after(tip);
        if (input.type === "checkbox") field.appendChild(tip);
      }
      tip.textContent = message;
      input.classList.add("wpcf7-not-valid");
      input.setAttribute("aria-invalid", "true");
    } else {
      if (tip) tip.remove();
      input.classList.remove("wpcf7-not-valid");
      input.removeAttribute("aria-invalid");
    }
  };

  $$(".js-demo-form", root).forEach((form) => {
    const box = form.closest(".js-fbox");
    const d = (k) => (box && box.dataset[k]) || "";

    const check = (input) => {
      const field = input.closest(".fld");
      if (!field) return true;
      let msg = "";
      if (input.type === "checkbox") {
        if (input.required && !input.checked) msg = d("msgCheck");
      } else {
        const v = input.value.trim();
        if (input.required && !v) msg = d("msgRequired");
        else if (v && input.type === "email" && !EMAIL.test(v)) msg = d("msgEmail");
        else if (v && input.type === "url" && !URL_RE.test(v)) msg = d("msgUrl");
      }
      setError(field, input, msg);
      return !msg;
    };

    // Clear/re-check a field as soon as the visitor fixes it.
    form.addEventListener("input", (e) => {
      if (e.target.matches(".wpcf7-not-valid")) check(e.target);
    });
    form.addEventListener("change", (e) => {
      if (e.target.matches(".wpcf7-not-valid")) check(e.target);
    });

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      let first = null;
      $$("input, select, textarea", form).forEach((input) => {
        if (!check(input) && !first) first = input;
      });
      if (first) {
        first.focus();
        toast(d("toastErrTitle"), d("toastErr"), "error");
        return;
      }
      const btn = $("button[type=submit]", form);
      if (btn) btn.disabled = true;
      setTimeout(() => {
        form.reset();
        if (btn) btn.disabled = false;
        toast(d("toastTitle"), d("toastOk"), "ok");
      }, RM ? 0 : 700);
    });
  });

    scrollFrame();
  };

  // ─── Page transitions (Barba.js) ────────────────────────────────────────────
  initPage($("main") || document);

  if (!RM && $('[data-barba="wrapper"]')) {
    // Curtain: four navy columns wipe up over the page, the brand mark flashes,
    // the new page is swapped in underneath, then the columns lift away.
    const curtain = document.createElement("div");
    curtain.className = "pt";
    curtain.setAttribute("aria-hidden", "true");
    curtain.innerHTML = '<span class="pt__bar"></span>'.repeat(4) + '<span class="pt__mark"></span>';
    document.body.appendChild(curtain);
    const bars = $$(".pt__bar", curtain);
    const mark = $(".pt__mark", curtain);
    const brandMark = $(".brand__mark");
    if (brandMark) mark.appendChild(brandMark.cloneNode(true));
    gsap.set(bars, { scaleY: 0, transformOrigin: "50% 100%" });
    gsap.set(mark, { opacity: 0, y: 12 });

    const cover = () => new Promise((resolve) => {
      curtain.classList.add("is-on");
      gsap.timeline({ onComplete: resolve })
        .set(bars, { transformOrigin: "50% 100%" })
        .to(bars, { scaleY: 1, duration: 0.7, ease: "power3.inOut", stagger: 0.08 })
        .to(mark, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, "-=0.3");
    });
    const reveal = () => new Promise((resolve) => {
      gsap.timeline({ onComplete: () => { curtain.classList.remove("is-on"); resolve(); } })
        .to(mark, { opacity: 0, y: -12, duration: 0.3, ease: "power2.in" })
        .set(bars, { transformOrigin: "50% 0%" })
        .to(bars, { scaleY: 0, duration: 0.8, ease: "power3.inOut", stagger: 0.08 }, "-=0.1");
    });

    // After a swap, copy the bits of chrome that differ per page.
    const syncChrome = (html) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      document.body.className = doc.body.className;
      const bar = $("#wpadminbar"), nextBar = doc.getElementById("wpadminbar");
      if (bar && nextBar) bar.innerHTML = nextBar.innerHTML;
      const current = new Set($$(".nav__list a[aria-current]", doc).map((a) => a.getAttribute("href")));
      $$(".nav__list a, .mpanel__list a").forEach((a) => {
        if (current.has(a.getAttribute("href"))) a.setAttribute("aria-current", "page");
        else a.removeAttribute("aria-current");
      });
    };

    barba.init({
      timeout: 8000,
      prevent: ({ el }) => {
        const href = el.getAttribute("href") || "";
        return (
          el.closest("#wpadminbar") ||
          href.startsWith("#") ||
          el.hasAttribute("download") ||
          (el.target && el.target !== "_self") ||
          /wp-admin|wp-login|wp-json|\/feed\/|\.(pdf|zip|jpe?g|png|gif|webp|svg)(\?|$)/i.test(href)
        );
      },
      transitions: [{
        name: "curtain",
        async leave() {
          closeMenu(true);
          if (lenis) lenis.stop();
          await cover();
          destroyPage();
        },
        async enter(data) {
          // Barba keeps the old container in the DOM until enter finishes; it is
          // already covered by the curtain, so take it out of the layout now.
          if (data.current && data.current.container) data.current.container.style.display = "none";
          window.scrollTo(0, 0);
          if (lenis) lenis.scrollTo(0, { immediate: true, force: true });
          syncChrome(data.next.html);
          // Re-bind Contact Form 7 on the new page, if the plugin is present.
          if (window.wpcf7 && typeof window.wpcf7.init === "function") {
            $$(".wpcf7 > form", data.next.container).forEach((f) => window.wpcf7.init(f));
          }
          initPage(data.next.container);
          scrollFrame();
          await reveal();
          if (lenis) lenis.start();
          const hash = window.location.hash;
          if (hash && $(hash)) {
            if (lenis) lenis.scrollTo($(hash), { offset: -100 });
            else $(hash).scrollIntoView();
          }
        },
      }],
    });
  }
});
